import { HttpClient } from '@adapters/HttpClient';
import type { GridData, HeatmapSlot, Rack, RouteResult, Warehouse } from '../models/types';

interface GridApiResponse {
  data: GridData;
}

interface RacksApiResponse {
  data: Rack[];
}

interface RouteApiResponse {
  data: RouteResult;
}

export interface PickingRouteRequest {
  location_ids: number[];
}

export interface SyncLayoutRequest {
  grid_rows?: number;
  grid_cols?: number;
  dock_x?: number;
  dock_y?: number;
  walls?: Array<{ x: number; y: number }>;
  racks?: Array<{
    row: number;
    col: number;
    is_endcap?: boolean;
    shelves?: number;
    slots_per_shelf?: number;
  }>;
}

interface SyncLayoutApiResponse {
  data: {
    warehouse: {
      id: number;
      name: string;
      grid_cols: number;
      grid_rows: number;
      cell_size_m: number;
      dock: { x: number; y: number };
    };
    racks: Rack[];
  };
  warnings?: { kept_racks: number[]; message: string };
}

interface WarehousesApiResponse {
  data: Warehouse[];
}

export class WarehouseMapApiAdapter {
  constructor(private httpClient: HttpClient) {}

  async getGrid(warehouseId: number): Promise<GridData> {
    const response = await this.httpClient.get<GridApiResponse>(
      `/warehouses/${warehouseId}/grid`,
      undefined,
      {},
      false,
    );
    return response.data;
  }

  async getRacks(warehouseId: number): Promise<Rack[]> {
    const response = await this.httpClient.get<RacksApiResponse>(
      `/warehouses/${warehouseId}/racks`,
      undefined,
      {},
      false,
    );
    return response.data;
  }

  async computePickingRoute(warehouseId: number, request: PickingRouteRequest): Promise<RouteResult> {
    const response = await this.httpClient.post<RouteApiResponse>(
      `/warehouses/${warehouseId}/picking-route`,
      request,
      false,
    );
    return response.data;
  }

  async syncLayout(
    warehouseId: number,
    request: SyncLayoutRequest,
  ): Promise<{ data: { warehouse: any; racks: Rack[] }; warnings?: { kept_racks: number[]; message: string } }> {
    const response = await this.httpClient.put<SyncLayoutApiResponse>(`/warehouses/${warehouseId}/layout`, request);
    return { data: response.data, warnings: response.warnings };
  }

  async getWarehouses(): Promise<Warehouse[]> {
    const response = await this.httpClient.get<WarehousesApiResponse>('/warehouses', undefined, {}, false);
    return response.data;
  }

  async getWarehouse(warehouseId: number): Promise<Warehouse> {
    const response = await this.httpClient.get<{ data: Warehouse }>(`/warehouses/${warehouseId}`, undefined, {}, false);
    return response.data;
  }

  async updateRack(
    warehouseId: number,
    rackId: number,
    payload: { shelves?: number; slots_per_shelf?: number },
  ): Promise<Rack> {
    const response = await this.httpClient.put<{ data: Rack }>(`/warehouses/${warehouseId}/racks/${rackId}`, payload);
    return response.data;
  }

  async getHeatmap(warehouseId: number): Promise<HeatmapSlot[]> {
    const response = await this.httpClient.get<{ data: HeatmapSlot[] }>(
      `/warehouses/${warehouseId}/heatmap`,
      undefined,
      {},
      false,
    );
    return response.data;
  }
}
